<template>
    <div class="row justify-content-center mt-4 pb-md-0 pb-5" v-if="currentForm">
        <div class="col-12">
            <transition name="fade" mode="out-in" appear>
                <div v-if="!stepOneComplete && !showIntroPages" key="stepOne">
                    <transition-card
                        :above-title="trans('profile.wizard.step_one.above_title')"
                        :description-top="trans('profile.wizard.step_one.description')"
                        image="/images/life-transitions-illustration-v2-02.svg"
                        :button-text="trans('profile.wizard.step_one.cta')"
                        @onClick="stepOneComplete = true"
                        :step="1"
                    />
                </div>
                <div v-else-if="!stepTwoComplete && !showIntroPages" key="stepTwo">
                    <transition-card
                        :above-title="trans('profile.wizard.step_two.above_title')"
                        :title="trans('profile.wizard.step_two.title')"
                        :description="trans('profile.wizard.step_two.description')"
                        image="/images/illustration_privacy.svg"
                        :button-text="trans('profile.wizard.step_two.cta')"
                        @onClick="stepTwoComplete = true"
                        :step="2"
                    />
                </div>
                <div v-else-if="currentScope" key="forms">
                    <b-alert v-if="formInfo.message" show dismissible variant="info" class="text-small">
                        {{formInfo.message}}
                    </b-alert>
                    <div class="d-flex align-items-center flex-column">
                        <img v-if="formInfo.image && formInfo.isTransition" :src="formInfo.image" width="120" class="mb-3" />
                        <div class="text-small font-weight-bold mb-2" v-if="isTransition">{{ currentForm.title }}</div>
                        <div v-if="formInfo.title && formInfo.isTransition" class="text-small font-weight-bold mb-2">{{formInfo.title}}</div>
                        <h5 v-if="formInfo.subtitle" class="text-center">{{formInfo.subtitle}}</h5>
                        <p v-if="formInfo.description" v-html="formInfo.description" class="text-center mb-2"/>
                    </div>
                    <transition name="fade" mode="out-in" v-on:enter="nextLoading = false">
                        <div :key="currentForm.code" v-if="!hasCurrentForm">
                            <user-choices
                                :choices="choiceData.choices"
                                @backClicked="currentScope = choiceData.scopeId"
                                v-if="choiceData && choiceData.choices.length > 0"/>
                            <h5 class="pb-2" v-if="!isTransition">{{ currentForm.title }}</h5>
                            <children
                                v-if="currentForm.code === 'children'"
                                :should-notify="false"
                                :is-wizard="true"
                                @onFormUpdated="onFormChange"
                                @onSubmitSuccess="() => getForm(currentId, 'next')"
                                :ref="currentForm.code"
                            />
                            <dynamic-profile-form
                                :should-notify="false"
                                :form="currentForm.fields"
                                :identifier="currentForm.code"
                                :is-wizard="true"
                                :validate-on-input="!formValid"
                                @onFormUpdated="onFormChange"
                                @onSubmitSuccess="() => getForm(currentId, 'next')"
                                @onSubmitFailed="submitFailed"
                                :ref="currentForm.code"
                                :subtitle="currentForm.subtitle"
                                :description="currentForm.description"
                                :is-transition="isTransition"
                                v-else
                            />
                            <div class="d-flex justify-content-between align-items-center py-4">
                                <div>
                                    <b-button variant="link" v-if="backScope" @click="onBackClicked">{{trans('global.back')}}</b-button>
                                </div>
                                <loading-button :button-text="trans('global.next')" @click="submitScope" :disabled="!formValid" :loading="nextLoading"/>
                            </div>
                        </div>
                    </transition>
                </div>
                <div v-else key="lastStep">
                    <div v-if="!isServiceActivation">
                        <transition-card
                            :above-title="trans('profile.wizard.end_no_activation.above_title')"
                            :title="trans('profile.wizard.end_no_activation.title')"
                            :description="trans('profile.wizard.end_no_activation.description')"
                            image="https://lifeed-cms.s3.eu-west-1.amazonaws.com/Property_1_illustration_profilingwizard_end1_00efd2137c.svg"
                            :button-text="trans('profile.wizard.end_no_activation.cta')"
                            @onClick="onActivationComplete"
                            :step="getLastStep"
                        />
                    </div>
                    <div v-else>
                        <transition-card
                            v-if="isBeforeLast"
                            :above-title="trans('profile.wizard.before_end.above_title')"
                            :title="trans('profile.wizard.before_end.title')"
                            :description="trans('profile.wizard.before_end.description')" 
                            image="/images/lifeed_logo.svg"
                            :step="getLastStep"
                            @onClick="hideBeforeLastStep"
                            isAutoSkip
                        />
                        <transition-card
                            v-else
                            :above-title="trans('profile.wizard.end.above_title')"
                            :title="trans('profile.wizard.end.title')"
                            :description="trans('profile.wizard.end.description')"
                            image="https://lifeed-cms.s3.eu-west-1.amazonaws.com/Property_1_illustration_profilingwizard_end2_f35571bbd5.svg"
                            :button-text="trans('profile.wizard.end.cta')"
                            @onClick="onActivationComplete"
                            :step="getLastStep"
                        />
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import DynamicProfileForm from "../forms/DynamicProfileForm";
import TransitionCard from "../../molecules/cards/TransitionCard";
import LoadingButton from "../../atoms/LoadingButton";
import roleMixin from "../../mixins/roleMixin";
import Children from "../forms/Children";
import UserChoices from "./UserChoices";
import {mapMutations, mapGetters, mapActions} from "vuex";

export default {
    name: "ProfileWizard",
    components: {UserChoices, Children, LoadingButton, TransitionCard, DynamicProfileForm},
    data(){
        return{
            currentScope: null,
            currentId: null,
            backScope: null,
            currentForm: null,
            formValid: true,
            service: null,
            nextLoading: false,
            stepOneComplete: false,
            stepTwoComplete: false,
            isBeforeLast: false,
            isLast: false,
            type: "",
            choiceData:null,
            showIntroPages: true,
            formInfo: {
                isTransition: false,
                image: '',
                title: '',
                subtitle: '',
                description: '',
                message: ''
            }
        }
    },
    mounted() {
        this.fetchWizardData();
    },
    methods:{
        fetchWizardData(){
            let url;
            let baseUrl = '/api/rest/profile/wizard';
            if(this.$route.query.type) {
                let queryUrl = `?type=${this.$route.query.type}&id=${this.$route.query.id}`
                url = baseUrl.concat(queryUrl)
            } else {
                url = baseUrl
            }
            this.$axios.get(url)
                .then(res => {
                    if(!res.data.current){
                        this.checkService(res.data.service, res.data.type);
                    }
                    else{
                        this.currentScope = res.data.current;
                        this.backScope = res.data.back;
                        this.currentId = res.data.path_id;
                        this.isLast = res.data.last;
                        this.type = res.data.type;
                        this.service = res.data.service;
                        this.showIntroPages = this.showIntroPages ? res.data.last : false
                    }
                })
                .catch(err => {
                    console.log(err);
                })
            trackEvent("ProfileWizardStart", {})
        },
        getScope(scope){
            this.$axios.get(`/api/rest/scope/${scope}`)
                .then(res => {
                    const scopeData =res.data.scopeData;
                    this.currentForm = scopeData;
                    this.choiceData = res.data.choices;
                    this.setCurrentStep(res.data.steps.current);
                    this.setTotalSteps(res.data.steps.total);
                    this.formInfo.label = scopeData.title ? scopeData.title : '';
                    this.formInfo.subtitle = scopeData.subtitle ? scopeData.subtitle : '';
                    this.formInfo.description = scopeData.description ? scopeData.description : '';
                    this.formInfo.image = scopeData.image ? scopeData.image : '';
                    this.formInfo.isTransition = scopeData.isTransition;
                    this.formInfo.message = scopeData.hasOwnProperty('message') ? scopeData.message : '';
                    this.scrollToTop();
                })
                .catch(err => {
                    console.log(err);
                })
        },
        async submitScope(){
            const form = this.$refs[this.currentForm.code];
            const valid = await form.$refs.observer.validate();

            if(valid){
                this.nextLoading = true;
                form.submit();
            }else{
                this.formValid = false;
            }
        },
        scrollToTop() {
            window.scrollTo(0,0);
        },
        submitFailed(){
            this.nextLoading = false;
        },
        onFormChange(valid){
            this.formValid = valid
        },
        onBackClicked(){
            this.getForm(this.currentId, 'back');
        },
        hideBeforeLastStep() {
            this.isBeforeLast = false
        },
        async getForm(id, action){
            if(this.isLast && action === 'next'){
                if(this.service && this.type === 'stream'){
                    this.checkProfiling();
                }else{
                    this.onWizardComplete();
                }
            }else{
                this.$axios.get(`/api/rest/profile/wizard/${action}?id=${id}`)
                    .then(res => {
                        this.currentScope = res.data.current;
                        this.backScope = res.data.back;
                        this.currentId = res.data.path_id;
                        this.isLast = res.data.last;
                        this.service = res.data.hasOwnProperty('service') ? res.data.service : this.service;
                        this.type = res.data.hasOwnProperty('type') ? res.data.type : this.type;
                    })
                    .catch(err => {
                        console.log(err);
                    })
            }
        },
        checkProfiling(){
            this.$axios.get(`/api/rest/profile/hasMainTag/${this.service}`)
                .then(res => {
                    if(res.data.done){
                        this.onWizardComplete();
                    }else{
                        const scopeData =res.data.scopeData;
                        this.currentForm = res.data.scopeData;
                        this.isBeforeLast = true;
                        this.formInfo.isTransition = res.data.scopeData.isTransition;
                        this.formInfo.label = scopeData.title ? scopeData.title : '';
                        this.formInfo.subtitle = scopeData.subtitle ? scopeData.subtitle : '';
                        this.formInfo.description = scopeData.description ? scopeData.description : '';
                    }
                })
                .catch(err => {
                    console.log(err);
                })
        },
        onWizardComplete(){
            this.currentScope = null;
            trackEvent("ProfileWizardCompleted", {})
        },
        async onActivationComplete(){
            await this.submitActivity();
            if(this.service){
                this.checkService(this.service, this.type);
            }else if(this.intendedUrl){
                this.$router.push(this.intendedUrl);
                this.setIntendedUrl('');
            }else{
                this.$router.push({name: 'servicedashboard'})
            }
        },
        async submitActivity() {
            return this.$axios.post('/api/rest/profile/wizard/complete')
        },
        ...mapActions({
            getMenu: 'user/getMenu'
        }),
        ...mapMutations({
            setCurrentStep: 'profile/setWizardCurrentStep',
            setTotalSteps: 'profile/setWizardTotalSteps',
            setIntendedUrl: 'user/setIntendedUrl'
        })
    },
    computed: {
        ...mapGetters({
            getLastStep: 'profile/getWizardTotalSteps',
            getUserId: 'user/getUserId',
            intendedUrl: 'user/getIntendedUrl'
        }),
        isTransition() {
            return this.formInfo.isTransition ? true : false;
        },
        isServiceActivation() {
            return this.service ? true : false
        },
        hasCurrentForm() {
            return Object.entries(this.currentForm).length === 0
        }
    },
    watch:{
        currentScope: function(val){
          if(val === 'profilingCompleted') {
            this.currentForm = {};
            this.getForm(this.currentId, 'next');
          }
          else if(val){
            this.getScope(val, 'next');
          }
        },
        stepTwoComplete: function(val){
            if(val){
                this.getScope(this.currentScope, 'next');
            }
        }
    },
    mixins: [roleMixin]
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity .3s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
